import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { History, ChevronDown, ChevronUp, ShieldAlert, RotateCcw, X } from "lucide-react";
import { AlertDispatchPanel } from "./AlertDispatchPanel";

// CSRF + cookies are added by the shared `api` axios instance — see
// /app/frontend/src/lib/api.js. We must NOT use raw axios here, or
// state-changing requests get rejected with "Jeton CSRF manquant".

// Source of truth duplicated from backend/alert_status.py — keep in sync.
// We allow the UI to grey-out invalid options but the server is the
// final authority and will reject illegal transitions with 400.
const ALLOWED_TRANSITIONS = {
  received: ["under_review", "suspect"],
  under_review: ["in_process", "responder_assigned", "rejected_false", "suspect", "resolved"],
  suspect: ["under_review", "in_process", "rejected_false", "resolved"],
  in_process: ["responder_assigned", "resolved"],
  responder_assigned: ["in_process", "resolved"],
  resolved: [], // reopen-only
  rejected_false: [], // reopen-only
};

const STATUS_META = {
  received:           { label: "Reçue",            color: "text-zinc-300 border-zinc-600" },
  under_review:       { label: "En examen",        color: "text-sky-300 border-sky-500/60" },
  in_process:         { label: "En traitement",    color: "text-cyan-300 border-cyan-500/60" },
  responder_assigned: { label: "Secours assignés", color: "text-amber-300 border-amber-500/60" },
  resolved:           { label: "Résolue",          color: "text-emerald-300 border-emerald-500/60" },
  rejected_false:     { label: "Rejetée (fausse)", color: "text-rose-300 border-rose-500/60" },
  suspect:            { label: "Suspect",          color: "text-orange-300 border-orange-500/60" },
};

const USER_FACING_META = {
  received:        "Reçue",
  under_review:    "En examen",
  in_process:      "En traitement",
  help_on_the_way: "Secours assignés",
  resolved:        "Résolue",
  closed:          "Clôturée",
};

const fmtTs = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }); }
  catch (_e) { return iso; }
};

export const AlertStatusControl = ({ alert, onStatusChanged }) => {
  const internal = alert.internal_status || "received";
  const userFacing = alert.user_facing_status || "received";
  const isTerminal = internal === "resolved" || internal === "rejected_false";
  const allowed = ALLOWED_TRANSITIONS[internal] || [];
  const meta = STATUS_META[internal] || STATUS_META.received;

  const [open, setOpen] = useState(false);
  // `userPickedTarget` is null until the operator picks something; we
  // derive `target` from it + the current allowed list during render so
  // this component never sets state in a useEffect (avoids the
  // react-hooks/set-state-in-effect anti-pattern).
  const [userPickedTarget, setUserPickedTarget] = useState(null);
  const target = (userPickedTarget && allowed.includes(userPickedTarget))
    ? userPickedTarget
    : (allowed[0] || "");
  const [adminNote, setAdminNote] = useState("");
  const [userMessage, setUserMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null); // null = not loaded yet
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/admin/alerts/${alert.id}/history`);
      setHistory(data.history || []);
    } catch (_e) {
      toast.error("Impossible de charger l'historique");
    }
  };
  const toggleHistory = async () => {
    if (history === null) await loadHistory();
    setShowHistory(v => !v);
  };

  const submit = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/alerts/${alert.id}/status`, {
        new_status: target,
        admin_note: adminNote.trim() || null,
        user_message: userMessage.trim() || null,
      });
      toast.success(`Statut: ${STATUS_META[data.internal_status]?.label || data.internal_status}`);
      onStatusChanged?.(data);
      setUserPickedTarget(null);
      setAdminNote("");
      setUserMessage("");
      setOpen(false);
      if (history !== null) await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du changement de statut");
    } finally { setBusy(false); }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/alerts/${alert.id}/reopen`, {
        admin_note: adminNote.trim() || "Réouverture",
        user_message: userMessage.trim() || null,
      });
      toast.success("Alerte rouverte");
      onStatusChanged?.(data);
      setAdminNote("");
      setUserMessage("");
      setOpen(false);
      if (history !== null) await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la réouverture");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-zinc-800 pt-3" data-testid={`admin-status-control-${alert.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
          <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 border ${meta.color}`}
                data-testid={`admin-status-internal-${alert.id}`}>
            {meta.label}
          </span>
          <span className="text-[9px] font-mono uppercase text-zinc-500 truncate" title={`Vu par le citoyen: ${USER_FACING_META[userFacing]}`}>
            citoyen voit · {USER_FACING_META[userFacing]}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={toggleHistory}
            data-testid={`admin-status-toggle-history-${alert.id}`}
            className="p-1 border border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
            aria-label="Historique"
            title="Historique"
          >
            <History className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            data-testid={`admin-status-toggle-${alert.id}`}
            className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-700 text-zinc-300 text-[10px] font-bold uppercase tracking-[0.15em] hover:border-emerald-500/60 hover:text-emerald-300"
          >
            {open ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>} Changer
          </button>
        </div>
      </div>

      {/* Iter55 Phase 2 — Central-Admin verification + dispatch + reassign.
          Sits above the legacy status pipeline so admins see it first. */}
      <div className="mt-2 border-t border-zinc-800 pt-2">
        <AlertDispatchPanel alert={alert} onUpdated={onStatusChanged} />
      </div>

      {open && (
        <div className="mt-2 border border-zinc-700 bg-zinc-900/60 p-2 space-y-2" data-testid={`admin-status-form-${alert.id}`}>
          {isTerminal ? (
            <div className="text-[10px] text-zinc-400 font-mono">
              Statut terminal — utiliser «&nbsp;Rouvrir&nbsp;» pour revenir à <span className="text-zinc-200">En examen</span>.
            </div>
          ) : (
            <>
              <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-zinc-500">Nouveau statut</label>
              <select
                value={target}
                onChange={(e) => setUserPickedTarget(e.target.value)}
                data-testid={`admin-status-select-${alert.id}`}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-[11px] font-mono px-2 py-1.5"
              >
                {allowed.map(s => (
                  <option key={s} value={s}>
                    {STATUS_META[s]?.label || s} · citoyen voit «&nbsp;{USER_FACING_META[
                      s === "rejected_false" || s === "suspect" ? "under_review"
                      : s === "responder_assigned" ? "help_on_the_way" : s
                    ]}&nbsp;»
                  </option>
                ))}
              </select>
            </>
          )}

          <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-zinc-500">Note interne (jamais vue par le citoyen)</label>
          <textarea
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            data-testid={`admin-status-note-${alert.id}`}
            placeholder="Pour l'équipe — confidentiel"
            rows={2}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-[11px] px-2 py-1.5 resize-none"
          />

          <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Message au citoyen <span className="text-zinc-600 normal-case">(optionnel — sinon message par défaut)</span>
          </label>
          <textarea
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            data-testid={`admin-status-user-msg-${alert.id}`}
            placeholder="Texte envoyé en notification au citoyen"
            rows={2}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-[11px] px-2 py-1.5 resize-none"
          />

          <div className="flex items-center justify-end gap-1.5 pt-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`admin-status-cancel-${alert.id}`}
              className="px-2 py-1 border border-zinc-700 text-zinc-400 text-[10px] font-bold uppercase tracking-[0.15em] hover:text-zinc-100"
            >
              <X className="w-3 h-3 inline" /> Annuler
            </button>
            {isTerminal ? (
              <button
                type="button"
                onClick={reopen}
                disabled={busy}
                data-testid={`admin-status-reopen-${alert.id}`}
                className="px-2.5 py-1 border border-cyan-500/60 text-cyan-300 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-cyan-500 hover:text-black disabled:opacity-50"
              >
                <RotateCcw className="w-3 h-3 inline mr-1" /> {busy ? "…" : "Rouvrir"}
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={busy || !target}
                data-testid={`admin-status-submit-${alert.id}`}
                className="px-2.5 py-1 border border-emerald-500/60 text-emerald-300 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-500 hover:text-black disabled:opacity-50"
              >
                {busy ? "…" : "Enregistrer"}
              </button>
            )}
          </div>
        </div>
      )}

      {showHistory && (
        <div className="mt-2 border border-zinc-800 bg-zinc-950/50 p-2 space-y-1.5" data-testid={`admin-status-history-${alert.id}`}>
          {(!history || history.length === 0) ? (
            <div className="text-[10px] text-zinc-500 font-mono">Pas encore d&apos;historique.</div>
          ) : history.map((h, idx) => {
            const m = STATUS_META[h.internal_status] || {};
            return (
              <div key={h.id || idx} className="text-[10px]">
                <div className="flex items-center justify-between gap-2">
                  <span className={`px-1 border ${m.color || "border-zinc-700 text-zinc-400"}`}>{m.label || h.internal_status}</span>
                  <span className="font-mono text-zinc-500">{fmtTs(h.created_at)}</span>
                </div>
                <div className="pl-1 mt-0.5 text-zinc-400">
                  <span className="text-zinc-500">par </span>{h.updated_by_admin_name || "—"}
                  {h.is_reopen && <span className="ml-1 text-cyan-400">[réouverture]</span>}
                </div>
                {h.admin_note && (
                  <div className="pl-1 text-zinc-500 italic">interne · {h.admin_note}</div>
                )}
                {h.user_message && (
                  <div className="pl-1 text-emerald-300/80">citoyen · {h.user_message}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AlertStatusControl;
